import React, { useEffect, useState } from "react";
import OtherPageHeader from "../../components/OtherHeader";
import { useForm, Controller } from "react-hook-form";
import {
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Card,
  Select,
  Radio,
  Checkbox,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AxiosInstance from "../../AxiosInstance";
import { getIpAddress } from "../../utils/getIpAddress";

const { Option } = Select;

// Static consents for Loan Application
const STATIC_CONSENTS = [
  {
    key: "politically_exposed_person",
    isMandatory: false,
    matchText: "politically exposed person",
    message: "I am not a politically exposed person or related to politically exposed person.",
  },
];

// Fetch from API only to resolve consentIds at submit time
const getLoanApplicationConsentsFromApi = async () => {
  try {
    const response = await AxiosInstance().get("/loan-consent/filter", {
      params: { screenName: "Loan Application", isActive: true },
    });
    if (response.data?.success) return response.data.data;
    return [];
  } catch (error) {
    return [];
  }
};

// Fetch user's existing consent statuses by mobile
const getUserConsentsByMobile = async (mobileNumber) => {
  try {
    const response = await AxiosInstance().get("/loan-consent/consents-by-mobile", {
      params: { mobileNumber },
    });
    if (response.data?.success) return response.data.data.consents;
    return [];
  } catch (error) {
    return [];
  }
};

export default function LoanForm({
  loanId: propLoanId,
  loanStepId: propLoanStepId,
  stepId: propStepId,
  embedded = false,
}) {
 
  const navigate = useNavigate();
  const location = useLocation();

  const loanId = propLoanId || location.state?.loanId;
  const loanStepId = propLoanStepId || location.state?.loanStepId;
  const stepId = propStepId || location.state?.stepId;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [natureOfBusinessOptions, setNatureOfBusinessOptions] = useState([]);
  const [purposeOfFacilityOptions, setPurposeOfFacilityOptions] = useState([]);
  const [purposeOfLoanValue, setPurposeOfLoanValue] = useState("");

  const [isFinanciallyDependent, setIsFinanciallyDependent] = useState(false);
  // consentStates keyed by STATIC_CONSENTS key
  const [consentStates, setConsentStates] = useState({});
  const [showConsentMsg, setShowConsentMsg] = useState(false);
  const [showFinDependentError, setShowFinDependentError] = useState(false);

  const [isSpouse, setIsSpouse] = useState(false);
  const [salutation, setSalutation] = useState("Mr.");

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      SME_SSI__c: "No",
    },
  });

  /* ---------------- OPTIONS ---------------- */

  const purposeOfLoanOptions = [
    { value: "Growth Capital / Working capital", label: "Growth Capital / Working capital" },
    { value: "Refinancing of existing debt", label: "Refinancing of existing debt" },
    { value: "General Corporate Purpose", label: "General Corporate Purpose" },
    { value: "Investments", label: "Investments" },
    { value: "Others", label: "Others" },
  ];

  const endUseOptions = [
    { value: "balance_transfer", label: "Balance Transfer" },
    { value: "balance_transfer_top_up", label: "Balance Transfer + Top up" },
    { value: "business_expansion", label: "Business Expansion" },
    { value: "business_use", label: "Business Use" },
    { value: "construction_purchase_new_units", label: "Construction/Purchase of new units" },
    { value: "education", label: "Education" },
    { value: "equipment_purchase", label: "Equipment Purchase" },
    { value: "hl_restructuring", label: "HL - Restructuring" },
    { value: "hl_top_up", label: "HL - Top Up (Additional)" },
    { value: "hl_total_loan", label: "HL - Total Loan" },
    { value: "home_construction", label: "Home Construction" },
    { value: "home_refinance", label: "Home Refinance" },
    { value: "insurance", label: "Insurance" },
    { value: "investments", label: "Investments" },
    { value: "loan_consolidation", label: "Loan Consolidation" },
    { value: "loan_substitution", label: "Loan Substitution" },
    { value: "mortgage_property_home_equity_loans", label: "Mortgage/Property/Home Equity Loans" },
    { value: "normal_hl", label: "Normal HL" },
    { value: "office_renovation", label: "Office Renovation" },
    { value: "personal_use", label: "Personal Use" },
    { value: "purchase_of_property", label: "Purchase of property" },
    { value: "purchasing_old_units_resale", label: "Purchasing old units (Resale)" },
    { value: "repair_renovation_existing_units", label: "Repair & Renovation of existing units" },
    { value: "travel", label: "Travel" },
    { value: "working_capital", label: "Working Capital" },
    { value: "other", label: "Other" },
    { value: "self_construction", label: "Self-construction" },
    { value: "direct_builder_purchase_b2b", label: "Direct builder purchase B2B" },
    { value: "machinery_purchase", label: "Machinery Purchase" },
    { value: "stock_purchase", label: "Stock Purchase" },
    { value: "purpose_higher_education", label: "Purpose for Higher Education" },
    { value: "rental_deposit_renewal", label: "Rental Deposit Renewal" },
    { value: "rental_deposit_90_days", label: "Rental Deposit 90 Days" },
    { value: "lsf_plus_plus", label: "LSF Plus Plus" },
    { value: "general_corporate_purpose", label: "General Corporate Purpose" }
  ];

  const applicantTypeOptions = [
    { value: "individual", label: "Individual" },
    { value: "proprietorship", label: "Proprietorship" },
    { value: "partnership", label: "Partnership" },
    { value: "company", label: "Company" },
  ];

  const occupationOptions = [
    { value: "salaried", label: "Salaried" },
    { value: "self_employed", label: "Self Employed" },
    { value: "retired", label: "Retired" },
    { value: "housewife", label: "Housewife" },
    { value: "student", label: "Student" },
  ];

  const annualIncomeOptions = [
    { value: "upto_5_lakhs", label: "Upto Rs. 5 Lakhs" },
    { value: "5_to_10_lakhs", label: "Rs. 5–10 Lakhs" },
    { value: "10_to_25_lakhs", label: "Rs. 10–25 Lakhs" },
    { value: "above_25_lakhs", label: "Above Rs. 25 Lakhs" },
  ];

  const relationshipOptions = [
    { value: "husband", label: "Husband" },
    { value: "wife", label: "Wife" },
    { value: "parents", label: "Parents" },
  ];

  /* ---------------- HANDLERS ---------------- */

  const handlePurposeOfLoanChange = (value) => {
    setPurposeOfLoanValue(value);
    setValue("Purpose_of_Loan__c", value);
    setValue("Purpose_of_Facility__c", "");

    if (value === "Investments") {
      setPurposeOfFacilityOptions([
        {
          value: "Investment in Financial assets",
          label: "Investment in Financial assets",
        },
      ]);
    } else if (value === "Others") {
      setPurposeOfFacilityOptions([{ value: "Personal use", label: "Personal use" }]);
    } else {
      setPurposeOfFacilityOptions([]);
    }
  };

  // Fetch API consents to get IDs, match with static consents, then submit/update
  const submitConsentData = async (userData) => {
    try {
      
      // Validate user data
      if (!userData || !userData.userId) {
        message.error("User data is incomplete. Please try again.");
        return;
      }
      const ipAddress = await getIpAddress();
      // Fetch policy details from LoanAgainstPolicy table using loanId
      let policyNumber = null;
      let policyId = null;
      try {
        const policyResponse = await AxiosInstance().get(`/loan-consent/by-loan/${loanId}`);
        if (policyResponse.data?.success && policyResponse.data.data) {
          policyNumber = policyResponse.data.data.policyNumber;
          policyId = policyResponse.data.data.policyId;
          }
      } catch (error) {
        console.error('LoanForm error fetching policy details:', error);
      }
      
      const apiConsents = await getLoanApplicationConsentsFromApi();
      
      // Get existing user consents to check if we need to update or create
      const existingUserConsents = await getUserConsentsByMobile(userData.phoneNumber);
      
      
      
      const consentTransactions = STATIC_CONSENTS.map((staticConsent) => {
        const apiMatch = apiConsents.find((c) =>
          c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "Loan Application"
        );
        
        if (!apiMatch) return null;
        
        // Check if this consent already exists for the user (either loan-specific or global)
        const existingConsent = existingUserConsents.find(uc => 
          uc.consentId === apiMatch.consentId && 
          (uc.loanId === parseInt(loanId) || uc.loanId === null)
        );
        
        const consentData = {
          consentId: apiMatch.consentId,
          consentStatus: consentStates[staticConsent.key] || false,
          isActive: true,
          userId: userData.userId,
          loanId: parseInt(loanId),
          policyNumber: policyNumber,
          policyId: policyId,
          mobileNumber: userData.phoneNumber,
          ipAddress:ipAddress
        };
        
        return {
          ...consentData,
          ipAddress:ipAddress,
          existingTransaction: existingConsent, // Will be used to determine create vs update
        };
      }).filter((t) => t && t.consentId !== null); // skip mismatched
      
      
      if (consentTransactions.length === 0) {
        return;
      }
      
      for (const consentTx of consentTransactions) {
        try {
          if (consentTx.existingTransaction) {
            // Update existing transaction using PUT endpoint
            const updateData = {
              consentStatus: consentTx.consentStatus,
            };
            const response = await AxiosInstance().put(`/loan-consent/transaction/${consentTx.existingTransaction.loanConsentId}`, updateData);

          } else {
            // Create new transaction using POST endpoint
            const createData = {
              ...consentTx,
              createdBy: String(userData.userId),
              UpdatedBy: null,
              ipAddress:ipAddress
            };
            delete createData.existingTransaction; // Remove helper property
            const response = await AxiosInstance().post("/loan-consent/transaction", createData);
          }
        } catch (error) {
          console.error('LoanForm error processing consent transaction:', error);
        }
      }
    } catch (error) {
      // Error handling without console logging
    }
  };

  const handleConsentChange = (key, checked) => {
    setConsentStates((prev) => ({ ...prev, [key]: checked }));
  };

  const areMandatoryConsentsAccepted = () =>
    STATIC_CONSENTS.filter((c) => c.isMandatory).every((c) => consentStates[c.key] === true);

  /* ---------------- STEP NAVIGATION ---------------- */

  const goToNextStep = async () => {
    const valid = await trigger([
      "Purpose_of_Loan__c",
      "End_Use__c",
      "ApplicantType__c",
      "Occupation_CKYC__c",
      "Acknowledgement_Mail_Recd__c",
      "month1_sal__c",
      "Marital_Status__c",
    ]);

    if (!valid) {
      message.error("Please complete all required fields");
      return;
    }
    setCurrentStep(2);
  };
  const getNatureOfBusinessOptions = (occupation) => {
    const businessOptions = [
      { value: 'retail_manufacturing', label: 'Retail & Manufacturing' },
      { value: 'medical_health', label: 'Medical & Health' },
      { value: 'agriculture', label: 'Agriculture' },
      { value: 'real_estate', label: 'Real Estate / Construction' },
      { value: 'banking_finance', label: 'Banking / Finance' },
      { value: 'it', label: 'Information Technology' },
      { value: 'hotel', label: 'Hotel Industry' },
      { value: 'others', label: 'Others (E-commerce, Education, Export-Import, Services etc.)' },
    ];
    return businessOptions;
  }

  /* ---------------- SUBMIT ---------------- */
  const handleOccupationChange = (value) => {
    setValue("Occupation_CKYC__c", value);
    setValue("Acknowledgement_Mail_Recd__c", "");
    setNatureOfBusinessOptions(getNatureOfBusinessOptions(value));
  };
  const onSubmit = async (data) => {
    
    try {
      // Check if all mandatory consents are accepted
      if (!areMandatoryConsentsAccepted()) {
        message.error("Please accept all mandatory consents");
        setShowConsentMsg(true);
        setTimeout(() => setShowConsentMsg(false), 3000);
        return;
      }

      // Get user data for consent submission
      const userResponse = await AxiosInstance().get("/user/me");
      const userData = userResponse.data;

      // Submit consent data first
      await submitConsentData(userData);

      const payload = {
        loanId,
        loanStepId,
        stepId,
        data: {
          ...data,
          salutation,
          declarationAccepted: consentStates.politically_exposed_person || false,
          isFinanciallyDependent:isFinanciallyDependent
        },
      };

      setLoading(true);

      const res = await AxiosInstance().post("/policy/loan/step", payload);

      if (res?.data?.statusCode === 200) {
        message.success("Loan application submitted successfully");
        // return;
        embedded ? window.location.reload() : navigate(`/phdashboard/loan/${loanId}`);
      } else {
        message.error("Loan application submitted with unexpected response");
      }
    } catch (err) {

      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- USE EFFECT ---------------- */

  useEffect(() => {
    const fetchUserAndPrefillConsents = async () => {
      try {
        const resp = await AxiosInstance().get("/user/me");
        const data = resp.data;
        
        // Prefill consent states from user's existing consents if mobile is available
        if (data.phoneNumber && data.phoneNumber.length === 10) {
          const userConsents = await getUserConsentsByMobile(data.phoneNumber);
          // Map API consentId back to static key via matchText
          const apiConsents = await getLoanApplicationConsentsFromApi();
          const newStates = {};
          STATIC_CONSENTS.forEach((staticConsent) => {
            const apiMatch = apiConsents.find((c) =>
              c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "Loan Application"
            );
            if (apiMatch) {
              const userMatch = userConsents.find((uc) => 
                uc.consentId === apiMatch.consentId && 
                (uc.loanId === parseInt(loanId) || uc.loanId === null)
              );
              if (userMatch) newStates[staticConsent.key] = userMatch.consentStatus;
            }
          });
          setConsentStates(newStates);
        }
      } catch (error) {
        // Error handling
      }
    };

    if (loanId) {
      fetchUserAndPrefillConsents();
    }
  }, [loanId]);

  /* ---------------- UI ---------------- */

  return (
    <div className="pan-validation-page">
      {!embedded && <OtherPageHeader />}

      <div className="container">
        <Card className="shadow-sm">
          <h2 className="text-center">Loan Application</h2>

          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            {currentStep === 1 && (
              <>
                <h3 className="section-title">Loan Application Details</h3>
                <Row gutter={[24, 16]}>
                  {/* Purpose of Loan */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Purpose of Loan"
                      validateStatus={errors.Purpose_of_Loan__c ? "error" : ""}
                      help={errors.Purpose_of_Loan__c?.message}
                      //   className="form-item"
                      required
                    >
                      <Controller
                        name="Purpose_of_Loan__c"
                        control={control}
                        rules={{ required: 'Please select purpose of loan' }}
                        render={({ field: { value, ...rest } }) => (
                          <Select
                            {...rest}
                            value={value}
                            onChange={handlePurposeOfLoanChange}
                            placeholder="Select Purpose of Loan"
                          // className="form-input"
                          >
                            {purposeOfLoanOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>
                  {/* Purpose of Facility */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Purpose of Facility"
                      validateStatus={errors.Purpose_of_Facility__c ? "error" : ""}
                      help={errors.Purpose_of_Facility__c?.message}
                      //   className="form-item"
                      required={purposeOfLoanValue === 'Investments' || purposeOfLoanValue === 'Others'}
                    >
                      <Controller
                        name="Purpose_of_Facility__c"
                        control={control}
                        rules={{
                          validate: value => {
                            if ((purposeOfLoanValue === 'Investments' || purposeOfLoanValue === 'Others') && !value) {
                              return 'Please select purpose of facility';
                            }
                            return true;
                          }
                        }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={
                              purposeOfLoanValue === 'Investments' || purposeOfLoanValue === 'Others'
                                ? 'Select Purpose of Facility'
                                : 'N/A - Select a different Purpose of Loan'
                            }
                            disabled={!purposeOfLoanValue ||
                              (purposeOfLoanValue !== 'Investments' && purposeOfLoanValue !== 'Others')}
                          // className="form-input"
                          >
                            {purposeOfFacilityOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* End Use */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="End Use"
                      validateStatus={errors.End_Use__c ? 'error' : ''}
                      help={errors.End_Use__c?.message}
                      required
                    >
                      <Controller
                        name="End_Use__c"
                        control={control}
                        rules={{ required: 'Please select end use' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select End Use"
                            style={{ width: '100%' }}
                          >
                            {endUseOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Applicant Type */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Applicant Type"
                      validateStatus={errors.ApplicantType__c ? 'error' : ''}
                      help={errors.ApplicantType__c?.message}
                      required
                    >
                      <Controller
                        name="ApplicantType__c"
                        control={control}
                        defaultValue="Individual"
                        rules={{ required: 'Please select applicant type' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select Applicant Type"
                            style={{ width: '100%' }}
                          >
                            {applicantTypeOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Occupation */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Occupation"
                      validateStatus={errors.Occupation_CKYC__c ? 'error' : ''}
                      help={errors.Occupation_CKYC__c?.message}
                      required
                    >
                      <Controller
                        name="Occupation_CKYC__c"
                        control={control}
                        rules={{ required: 'Please select occupation' }}
                        render={({ field: { value, ...rest } }) => (
                          <Select
                            {...rest}
                            value={value}
                            onChange={handleOccupationChange}
                            placeholder="Select Occupation"
                            style={{ width: '100%' }}
                          >
                            {occupationOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Nature of Business */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Nature of Business / Source of Income"
                      validateStatus={errors.Acknowledgement_Mail_Recd__c ? 'error' : ''}
                      help={errors.Acknowledgement_Mail_Recd__c?.message}
                      required
                    >
                      <Controller
                        name="Acknowledgement_Mail_Recd__c"
                        control={control}
                        rules={{ required: 'Please select nature of business/source of income' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={
                              getValues('Occupation_CKYC__c')
                                ? 'Select an option'
                                : 'Please select Occupation first'
                            }
                            disabled={!getValues('Occupation_CKYC__c')}
                            style={{ width: '100%' }}
                          >
                            {natureOfBusinessOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Annual Income */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Annual Income"
                      validateStatus={errors.month1_sal__c ? 'error' : ''}
                      help={errors.month1_sal__c?.message}
                      required
                    >
                      <Controller
                        name="month1_sal__c"
                        control={control}
                        rules={{ required: 'Please select annual income' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select Annual Income"
                            style={{ width: '100%' }}
                          >
                            {annualIncomeOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Qualification */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Qualification"
                      validateStatus={errors.Qualification__c ? 'error' : ''}
                      help={errors.Qualification__c?.message}
                    >
                      <Controller
                        name="Qualification__c"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select Qualification"
                            style={{ width: '100%' }}
                          >
                            <option value="">Select</option>
                            <option value="ILLITERATE">ILLITERATE</option>
                            <option value="PS">PS</option>
                            <option value="HS">HS</option>
                            <option value="UG">Graduate</option>
                            <option value="PG">PG</option>
                            <option value="PROFESSIONAL">PROFESSIONAL</option>
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>


                  {/* Marital Status */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Marital Status"
                      validateStatus={errors.Marital_Status__c ? 'error' : ''}
                      help={errors.Marital_Status__c?.message}
                      required
                    >
                      <Controller
                        name="Marital_Status__c"
                        control={control}
                        defaultValue="Married"
                        rules={{ required: 'Please select marital status' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select Marital Status"
                            style={{ width: '100%' }}
                            onChange={(value) => {
                              field.onChange(value);
                              if (value !== 'Married') {
                                setIsSpouse(false);
                              }
                            }}
                          >
                            <Option value="Single">Single</Option>
                            <Option value="Married">Married</Option>
                            <Option value="Divorced">Divorced</Option>
                            <Option value="Widowed">Widowed</Option>
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <div className="text-center mt-4">
                  <Button type="primary" onClick={goToNextStep}>
                    Next →
                  </Button>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>
                <Row gutter={[24, 16]}>
                  <Col xs={24}>
                    <h3 className="section-title">Family Details</h3>
                  </Col>

                  {/* Spouse/Father Radio Group */}
                  <Col xs={24} md={12}>
                    <Form.Item label="Relationship Type" required>
                      <Radio.Group
                        value={isSpouse ? 'spouse' : 'father'}
                        onChange={(e) => setIsSpouse(e.target.value === 'spouse')}
                      // disabled={getValues('Marital_Status__c') !== 'Married'}
                      >
                        <Radio value="father">Father's Details</Radio>
                        <Radio value="spouse"
                        //  disabled={getValues('Marital_Status__c') !== 'Married'}
                        >
                          Spouse's Details
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  {/* Spouse/Father First Name */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={isSpouse ? "Spouse's First Name" : "Father's First Name"}
                      validateStatus={isSpouse ? (errors.Spouse_First_Name__c ? 'error' : '') : (errors.Father_First_Name__c ? 'error' : '')}
                      help={isSpouse ? errors.Spouse_First_Name__c?.message : errors.Father_First_Name__c?.message}
                      required
                    >
                      <Controller
                        name={isSpouse ? "Spouse_First_Name__c" : "Father_First_Name__c"}
                        control={control}
                        rules={{ required: `Please enter ${isSpouse ? "spouse's" : "father's"} first name` }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder={`Enter ${isSpouse ? "spouse's" : "father's"} first name`}
                            // disabled={getValues('Marital_Status__c') === 'Single'}
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </Form.Item>
                  </Col>
                  {/* Spouse/Father Last Name */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={isSpouse ? "Spouse's Last Name" : "Father's Last Name"}
                      validateStatus={isSpouse ? (errors.Spouse_Last_Name__c ? 'error' : '') : (errors.Father_Last_Name__c ? 'error' : '')}
                      help={isSpouse ? errors.Spouse_Last_Name__c?.message : errors.Father_Last_Name__c?.message}
                      required
                    >
                      <Controller
                        name={isSpouse ? "Spouse_Last_Name__c" : "Father_Last_Name__c"}
                        control={control}
                        rules={{ required: `Please enter ${isSpouse ? "spouse's" : "father's"} last name` }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder={`Enter ${isSpouse ? "spouse's" : "father's"} last name`}
                            // disabled={getValues('Marital_Status__c') === 'Single'}
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Mother's First Name */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Mother's First Name"
                      validateStatus={errors.Mother_First_Name__c ? 'error' : ''}
                      help={errors.Mother_First_Name__c?.message}
                      required
                    >
                      <Controller
                        name="Mother_First_Name__c"
                        control={control}
                        rules={{ required: "Please enter mother's first name" }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter mother's first name"
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Mother's Last Name */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Mother's Last Name"
                      validateStatus={errors.Mother_Last_Name__c ? 'error' : ''}
                      help={errors.Mother_Last_Name__c?.message}
                      required
                    >
                      <Controller
                        name="Mother_Last_Name__c"
                        control={control}
                        rules={{ required: "Please enter mother's last name" }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter mother's last name"
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Residence Type */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Residence Type"
                      validateStatus={errors.residence_type__c ? 'error' : ''}
                      help={errors.residence_type__c?.message}
                      required
                    >
                      <Controller
                        name="residence_type__c"
                        control={control}
                        rules={{ required: 'Please select residence type' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select Residence Type"
                            style={{ width: '100%' }}
                          >
                            <Option value="Owned">Owned</Option>
                            <Option value="Rented">Rented</Option>
                            <Option value="Company Provided">Company Provided</Option>
                            <Option value="Leased">Leased</Option>
                            <Option value="Others">Others</Option>
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>
                  {/* Hidden salutation fields */}
                  <div style={{ display: 'none' }}>
                    <Controller
                      name="Father_Spouse_Salutation__c"
                      control={control}
                      defaultValue="Mr."
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                    <Controller
                      name="Mother_Salutation__c"
                      control={control}
                      defaultValue="Mrs."
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                  </div>
                  {/* SME/SSI */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Financially Dependent"
                      validateStatus={errors.SME_SSI__c ? 'error' : ''}
                      help={errors.SME_SSI__c?.message}
                      required
                    >
                      <Controller
                        name="SME_SSI__c"
                        control={control}
                        defaultValue="No"
                        render={({ field: { value, ...rest } }) => (
                          <Select
                            {...rest}
                            value={value}
                            onChange={(newValue) => {
                              rest.onChange(newValue);
                              setIsFinanciallyDependent(newValue === 'Yes');
                              if (newValue === 'No') {
                                setValue('Relationship_Borrower__c', '');
                              }
                            }}
                            placeholder="Select an option"
                            style={{ width: '100%' }}
                          >
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>

                  {/* Financially Dependent On (conditional) */}
                  {isFinanciallyDependent && (
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Financially dependent on"
                        validateStatus={errors.Relationship_Borrower__c ? 'error' : ''}
                        help={errors.Relationship_Borrower__c?.message}
                        required
                      >
                        <Controller
                          name="Relationship_Borrower__c"
                          control={control}
                          rules={{
                            validate: value => !isFinanciallyDependent || value ? true : 'Please select relationship'
                          }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder="Select Relationship"
                              style={{ width: '100%' }}
                            >
                              {relationshipOptions.map((option) => (
                                <Option key={option.value} value={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          )}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {/* Salutation */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Salutation"
                      validateStatus={errors.Salutation__c ? 'error' : ''}
                      help={errors.Salutation__c?.message}
                      required
                    >
                      <Controller
                        name="Salutation__c"
                        control={control}
                        rules={{ required: 'Please select salutation' }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select Salutation"
                            style={{ width: '100%' }}
                            onChange={(value) => {
                              field.onChange(value);
                              setSalutation(value);
                            }}
                          >
                            <Option value="Mr.">Mr.</Option>
                            <Option value="Ms.">Ms.</Option>
                            <Option value="Mrs.">Mrs.</Option>
                            <Option value="Dr.">Dr.</Option>
                          </Select>
                        )}
                      />
                    </Form.Item>
                  </Col>


                  {/* Loan Application Consents */}
                  <Col span={24}>
                    <div className="mb-3">
                      {STATIC_CONSENTS.map((consent) => (
                        <div key={consent.key} style={{ marginBottom: "10px" }}>
                          <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", margin: 0 }}>
                            <input
                              type="checkbox"
                              checked={consentStates[consent.key] || false}
                              onChange={(e) => handleConsentChange(consent.key, e.target.checked)}
                              style={{ marginTop: "3px", flexShrink: 0, width: "14px", height: "14px", cursor: "pointer" }}
                            />
                            <span style={{ fontSize: "13px", lineHeight: "1.4", textAlign: "left" }}>
                              {consent.message}
                              {consent.isMandatory && (
                                <span style={{ color: "red", marginLeft: "4px" }}>* (Required)</span>
                              )}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                    
                    {showConsentMsg && (
                      <div style={{ marginTop: "10px", fontSize: "14px", color: "red", textAlign: "center" }}>
                        Please agree to the consent terms to proceed.
                      </div>
                    )}
                    <div><span style={{ color: "red" }}>*</span> Sanctioning of loan shall be at sole discretion of Bajaj Finance Ltd. and shall be subject to due Assignment.</div>
                  </Col>
                 


                  <Col span={24} className="text-center mt-4">
                    <Button onClick={() => setCurrentStep(1)} style={{ marginRight: 12 }}>
                      ← Back
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      className="submit-btn"
                      loading={loading}
                    >
                      Submit Application
                    </Button>
                  </Col>
                </Row>


              </>
            )}
          </Form>
        </Card>
      </div>
    </div>
  );
}
